import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLatestMacroRegime } from '@/hooks/useMacroRegime';
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from 'recharts';
import { Activity } from 'lucide-react';

function regimeStyle(r: string | undefined) {
  switch (r) {
    case 'expansion':
      return { label: 'EXPANSION', cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' };
    case 'contraction':
      return { label: 'CONTRACTION', cls: 'bg-amber-500/15 text-amber-500 border-amber-500/30' };
    default:
      return { label: 'TRANSITION', cls: 'bg-muted text-muted-foreground border-border' };
  }
}

function useSparkline() {
  return useQuery({
    queryKey: ['macro-sparkline-52w'],
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - 7 * 53);
      const { data, error } = await (supabase as any)
        .from('liquidity_series_cache')
        .select('week_ending, glp_variants, btc_close_usd')
        .gte('week_ending', since.toISOString().slice(0, 10))
        .order('week_ending', { ascending: true });
      if (error) throw error;
      return (data || [])
        .map((r: any) => ({
          week: r.week_ending,
          v2: r.glp_variants?.V2 ?? null,
          btc: r.btc_close_usd ?? null,
        }))
        .filter((r: any) => r.v2 !== null && r.btc !== null);
    },
    staleTime: 30 * 60 * 1000,
  });
}

export function MacroRegimePanel() {
  const { data: row, isLoading } = useLatestMacroRegime();
  const { data: spark } = useSparkline();

  if (isLoading) {
    return (
      <section className="container mx-auto px-4 mt-6 mb-2">
        <Card><CardContent className="p-5"><Skeleton className="h-24 w-full" /></CardContent></Card>
      </section>
    );
  }
  if (!row) return null;

  const rs = regimeStyle(row.regime);
  const strengthLabel = (row.signal_strength === 'moderate' ? 'moderate signal' : 'weak signal');
  const yoy = row.net_liq_yoy_pct ?? 0;
  const yoyStr = `${yoy >= 0 ? '+' : ''}${yoy.toFixed(2)}%`;
  const note = row.observation_note ?? '';
  const truncated = note.length > 220 ? note.slice(0, 217) + '…' : note;

  return (
    <section className="container mx-auto px-4 mt-6 mb-2">
      <Card className="border-border/60">
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  Macro context
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge className={`${rs.cls} border font-bold`}>{rs.label}</Badge>
                <span className="text-xs text-muted-foreground">{strengthLabel}</span>
              </div>
              <p className="text-sm font-medium mb-2">
                Net liquidity {row.regime} · YoY {yoyStr}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {truncated}{' '}
                {note.length > 220 && (
                  <Link to="/macro" className="text-primary hover:underline">
                    Read full macro report
                  </Link>
                )}
              </p>
            </div>
            <div className="w-full md:w-64 h-24 shrink-0">
              {spark && spark.length > 4 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={spark}>
                    <YAxis yAxisId="v2" hide domain={['auto', 'auto']} />
                    <YAxis yAxisId="btc" hide scale="log" domain={['auto', 'auto']} />
                    <Tooltip
                      contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 11 }}
                      formatter={(value: any, name: string) => [Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 }), name]}
                      labelFormatter={(l) => `Week ${l}`}
                    />
                    <Line yAxisId="v2" type="monotone" dataKey="v2" name="Net liq / DXY" stroke="hsl(var(--primary))" dot={false} strokeWidth={1.5} />
                    <Line yAxisId="btc" type="monotone" dataKey="btc" name="BTC (log)" stroke="hsl(var(--muted-foreground))" dot={false} strokeWidth={1.5} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                  Chart loads after the next refresh
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground mt-2 px-1">
        Macro context for the picks on this page. Not a signal to act on. TOSS scores are unaffected by this panel.
      </p>

    </section>
  );
}

export default MacroRegimePanel;
